import { heartbeatService, thirdPartyWithLayoutService } from '@bedrock/fido-sdk';
import { heartbeat } from '@bedrock-rdk/config';
import { DEFAULT_HEARTBEAT_INTERVAL, MAX_HEARTBEAT_INTERVAL } from '../../../logic/heartbeat/heartbeat.constants';

const isValidInterval = value => Number.isFinite(value) && value > 0 && value <= MAX_HEARTBEAT_INTERVAL;

export class HeartbeatEngine {
  constructor() {
    this._reset();
  }

  _reset() {
    this._sendView = undefined;
    this._viewData = undefined;

    clearInterval(this._ticker);
    this._ticker = undefined;
    this._currentPlayerTime = 0;
    this._previousPlayerTime = undefined;
    this._previousRelativeTime = undefined;
    this._previousTickTime = this._currentTimeInSeconds();

    this._cslErrorHandler = undefined;
    this._sequenceNumber = 0;
  }

  _currentTimeInSeconds() {
    return Math.round(Date.now() / 1000);
  }

  get currentPlayerTime() {
    return this._currentPlayerTime;
  }

  set currentPlayerTime(value) {
    if (Number.isFinite(value) && value >= 0) {
      this._currentPlayerTime = Math.floor(value);
    }
  }

  async createSession(data) {
    try {
      const { session, polling, cslErrorHandler, view, startTime } = data;
      const { UID, ...sessionData } = session;
      const playerStartTime = Number.isFinite(startTime) ? startTime : 0;

      this._reset();

      // The "polling" parameter specifies seconds
      const pollingInterval = Number.isFinite(polling) ? polling * 1000 : undefined;

      if (isValidInterval(pollingInterval)) {
        this._heartbeatInterval = pollingInterval;
      } else if (isValidInterval(heartbeat.heartbeatInterval)) {
        this._heartbeatInterval = heartbeat.heartbeatInterval;
      } else {
        this._heartbeatInterval = DEFAULT_HEARTBEAT_INTERVAL;
      }

      this._currentPlayerTime = Math.floor(playerStartTime);
      this._viewData = view;
      this._cslErrorHandler = cslErrorHandler;

      const { sessionId, sendView } = await heartbeatService.createSession(
        UID
          ? {
              sessionData,
              user: { UID },
            }
          : {
              sessionData,
            },
      );
      // eslint-disable-next-line no-console
      console.log('[Heartbeat] Session created successfully:', sessionId);
      this._sendView = sendView;
      this._previousTickTime = this._currentTimeInSeconds();

      return sessionId;
    } catch (error) {
      // TODO: Figure out what is expected in case of Heartbeat session creation error
      // eslint-disable-next-line no-console
      console.error('[Heartbeat] Session creation failed:', error);

      return null;
    }
  }

  resume() {
    if (!this._ticker) {
      // Pause time should not be counted as viewed
      this._previousTickTime = this._currentTimeInSeconds();
      this._ticker = setInterval(() => this.sendHeartbeat(), this._heartbeatInterval);
    }

    this.sendHeartbeat();
  }

  pause() {
    // Sent before stopping the ticker to count the time played since the last tick
    this.sendHeartbeat();

    clearInterval(this._ticker);
    this._ticker = undefined;
  }

  async sendHeartbeat() {
    if (!this._sendView || !this._viewData) {
      return;
    }

    const currentTime = this._currentTimeInSeconds();
    const currentPlayerTime = this._currentPlayerTime;
    // Paused time is not counted, frozen timers and clock jumps are capped to the interval
    const tcRelative = this._ticker
      ? Math.min(Math.max(currentTime - this._previousTickTime, 0), Math.floor(this._heartbeatInterval / 1000))
      : 0;
    const sequenceNumber = this._sequenceNumber;

    // NOTE: sequency number is increased before sending the event to mitigate long-waiting promise resolution
    this._sequenceNumber += 1;
    this._previousTickTime = currentTime;
    this._previousPlayerTime = currentPlayerTime;
    this._previousRelativeTime = tcRelative;

    try {
      const { view_information: viewInformation } = await this._sendView({
        ...this._viewData,
        tc: currentPlayerTime,
        tcRelative,
        sequenceNumber,
      });

      if (viewInformation && viewInformation.csl && viewInformation.csl.allowed === false) {
        if (this._cslErrorHandler) {
          this._cslErrorHandler();
        }

        thirdPartyWithLayoutService.sendErrorEvent(new Error('Heartbeat failed due to CSL error'));
      }
    } catch (error) {
      thirdPartyWithLayoutService.sendErrorEvent(new Error(`Heartbeat tick failed: ${error.message}`));
    }
  }

  destroy() {
    // Per the spec, heartbeats should be sent when a player is closed
    this.sendHeartbeat();
    this._reset();
  }
}

export default new HeartbeatEngine();
